import { action, computed, makeObservable, observable } from 'mobx';
import {
  AllFilters,
  ChangeValueOptions,
  ComputedQuery,
  ComputedQueryFull,
  Filter,
  FiltersMap,
} from '../model';
import { initialCursor, Pagination } from './Pagination';
import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';
import { EventEmitter } from 'events';
import { History } from 'history';
import { StringFilterState } from './StringFilterState';
import { SortState } from './SortState';
import { addEventListener, TypedEmitter } from '../util';
import { hashQueryKey } from 'react-query/core';
import { QueryHistoryState } from './QueryHistoryState';

export interface QueryStateParams<F extends FiltersMap = FiltersMap> {
  history?: History;
  filters: F;
  search?: Filter<string>;
  limit?: number;
  refreshOnColumnsChange?: boolean;
  extra?: string;
}

export type QueryStateEvents = {
  change: () => void;
  refresh: (opts?: { clearResult?: boolean }) => void;
  scheduleRefresh: (opts?: { clearResult?: boolean }) => void;
  persist: () => void;
};

export class QueryState<F extends FiltersMap = FiltersMap> {
  readonly history?: History;
  readonly pagination: Pagination;
  customFilters: F;
  sort: SortState;
  _filters: AllFilters<F>;
  readonly events = new EventEmitter() as TypedEmitter<QueryStateEvents>;

  readonly queryHistory: QueryHistoryState<F> | null;
  readonly refreshOnColumnsChange;

  _columns = observable.set<string>([], { deep: false });
  _fields = observable.set<string>([], { deep: false });

  _initializedFromHistory?: boolean;

  _skipFilterListeners?: boolean;

  _extra?: string;

  constructor(params: QueryStateParams<F>) {
    this.refreshOnColumnsChange = params.refreshOnColumnsChange;
    this.pagination = new Pagination({ limit: params.limit });

    this.history = params.history;
    this.sort = new SortState({});

    this.customFilters = mapValues(params.filters, (filter, name) => {
      if (filter) {
        filter.name = name;
      }
      return filter;
    }) as F;

    this._filters = {
      ...this.customFilters,
      search:
        params.search ??
        new StringFilterState({
          name: 'search',
          defaultValue: '',
        }),
    };

    this.queryHistory = this.history
      ? new QueryHistoryState({
          query: this,
          history: this.history,
        })
      : null;

    this._extra = params.extra;

    makeObservable(this, {
      changeLimit: action,
      reset: action,
      toComputed: action,
      _filters: observable.ref,
      customFilters: observable.ref,
      addFilterIfNeeded: action,
      asComputed: computed,
      activeFilters: computed,
      nonPersistentFilters: computed,
      nonPersistentFiltersEntries: computed,
      nonPersistentFiltersMap: computed,
      activeFiltersEntries: computed,
      activeFiltersCount: computed,
      hasActiveFilters: computed,
      hasNonPersistentActiveFilters: computed,
      resetFilters: action.bound,
      resetPage: action.bound,
      filtersKey: computed,
      filtersKeyHash: computed,
      fields: computed,
      fieldsKey: computed,
      fieldsKeyHash: computed,
      hasNonPersistentCustomFilters: computed,
      nonPersistentCustomFiltersCount: computed,
      setExtra: action.bound,
    });
  }

  init({ skipFilterListeners }: { skipFilterListeners?: boolean } = {}) {
    this._skipFilterListeners = skipFilterListeners;
    const disposers = [
      ...Object.values(this.filtersEntries).flatMap(([_, filter]) => {
        return this.addFilterListeners(filter);
      }),
      this.queryHistory?.init(),
    ];

    return () => {
      disposers.forEach((d) => d?.());
    };
  }

  get limit() {
    return this.pagination.limit;
  }

  get fields() {
    return [
      ...Array.from(this._columns.values()),
      ...Array.from(this._fields.values()),
    ];
  }

  get fieldsKey() {
    return this.fields.sort();
  }

  get fieldsKeyHash() {
    return this.fieldsKey.join('');
  }

  get filtersKey() {
    const { customFilters } = this;

    return mapValues(customFilters, (filter) =>
      filter != null
        ? filter.isEmpty
          ? null
          : filter.toArray.map(filter.itemKey)
        : undefined,
    );
  }

  get filtersKeyHash() {
    return hashQueryKey([this.filtersKey]);
  }

  changeLimit(limit: number) {
    this.pagination.changeLimit(limit);
  }

  public reset() {
    this.pagination.moveToPage(0);
  }

  private _toComputed(): ComputedQueryFull<F> {
    const {
      pagination,
      customFilters,
      nonPersistentFiltersMap,
      search: searchFilter,
      sort,
      hasActiveFilters,
      hasNonPersistentActiveFilters,
      fields,
      refreshOnColumnsChange,
      extra,
    } = this;
    const { page, limit, offset, cursor } = pagination;

    const filters: ComputedQuery<F>['filters'] = mapValues(
      customFilters,
      (filter) =>
        filter != null ? (filter.isEmpty ? undefined : filter.value) : filter,
    );
    const rawFilters: ComputedQuery<F>['rawFilters'] = mapValues(
      customFilters,
      (filter) => (filter != null ? filter.value : filter),
    );

    const filtersKey = mapValues(customFilters, (filter) =>
      filter != null
        ? filter.isEmpty
          ? null
          : filter.toArray.map(filter.itemKey)
        : undefined,
    );

    const nonPersistentFiltersKey = mapValues(
      nonPersistentFiltersMap,
      (filter) =>
        filter != null
          ? filter.isEmpty
            ? null
            : filter.toArray.map(filter.itemKey)
          : undefined,
    );

    const rawSearch = searchFilter.value;
    const search = searchFilter.isEmpty ? undefined : rawSearch;

    return {
      page,
      limit,
      sort: sort.value.map(({ field: fieldName, direction: order }) => ({
        fieldName,
        order,
      })),
      offset,
      cursor: cursor == null || typeof cursor === 'string' ? cursor : null,
      rawFilters,
      filtersKey,
      nonPersistentFiltersKey,
      filters,
      rawSearch,
      search,
      hasActiveFilters,
      hasNonPersistentActiveFilters,
      columns: fields,
      fields: refreshOnColumnsChange
        ? fields
        : Array.from(this._fields.values()),
      extra,
    };
  }

  toComputed(): ComputedQueryFull<F> {
    return this._toComputed();
  }

  get asComputed(): ComputedQueryFull<F> {
    return this._toComputed();
  }

  get filtersArray() {
    const { _filters } = this;
    return Object.values(_filters).filter(
      (e) => e != null,
    ) as Filter<unknown>[];
  }

  get nonPersistentFilters() {
    const { filtersArray } = this;
    return filtersArray.filter((filter) => !filter.persistent);
  }

  get filtersEntries() {
    const { _filters } = this;
    return Object.entries(_filters).filter(
      (entry): entry is [string, Filter<unknown>] => entry[1] != null,
    );
  }

  get nonPersistentFiltersEntries() {
    const { filtersEntries } = this;
    return filtersEntries.filter(([_, filter]) => !filter.persistent);
  }

  get nonPersistentCustomFiltersCount() {
    return Object.values(this.nonPersistentCustomFilters).reduce(
      (size, filter) => size + filter.size,
      0,
    );
  }

  get hasNonPersistentCustomFilters() {
    return this.nonPersistentCustomFiltersCount > 0;
  }

  get nonPersistentCustomFilters() {
    const { customFilters } = this;
    return Object.values(customFilters).filter(
      (e) => e != null && !e?.persistent,
    ) as Filter<unknown>[];
  }

  get nonPersistentFiltersMap() {
    const { customFilters } = this;
    return pickBy(customFilters, (filter) => !filter?.persistent) as Partial<F>;
  }

  get activeFiltersEntries() {
    const { nonPersistentFiltersEntries } = this;
    return nonPersistentFiltersEntries.filter(([_, filter]) => !filter.isEmpty);
  }

  get activeFilters() {
    const { nonPersistentFilters } = this;
    return nonPersistentFilters.filter((filter) => !filter.isEmpty);
  }

  get activeFiltersCount() {
    return this.activeFilters.reduce((size, filter) => size + filter.size, 0);
  }

  get hasActiveFilters() {
    return (
      this.filtersArray.reduce((size, filter) => size + filter.size, 0) > 0
    );
  }

  get hasNonPersistentActiveFilters() {
    return this.activeFiltersCount > 0;
  }

  get hasActiveSort() {
    return this.sort.value.length > 0;
  }

  addFilterIfNeeded<K extends keyof AllFilters<F>>(
    key: K,
    filter: AllFilters<F>[K],
  ) {
    if (filter === undefined || this._filters[key]) {
      return;
    }
    this._filters = {
      ...this._filters,
      [key]: filter,
    };
    this.customFilters = {
      ...this.customFilters,
      [key]: filter,
    };
    const disposers = this.addFilterListeners(filter);
    return () => {
      disposers.forEach((d) => d?.());
    };
  }

  resetFilters(options?: ChangeValueOptions) {
    const { nonPersistentFilters } = this;
    nonPersistentFilters.forEach((filter) => {
      filter?.reset(options);
    });
  }

  resetCustomFilters(options?: ChangeValueOptions) {
    const { nonPersistentCustomFilters } = this;
    nonPersistentCustomFilters.forEach((filter) => {
      filter?.reset(options);
    });
  }

  resetSorting(params: { emitChangeEvent?: boolean } = {}) {
    this.sort.reset(params);
  }

  resetPage() {
    const { pagination } = this;
    pagination.page = 1;
    pagination.cursor = initialCursor;
  }

  get search() {
    return this._filters.search;
  }

  get filters(): AllFilters<F> {
    return this._filters;
  }

  get extra() {
    return this._extra;
  }

  setExtra(extra: string | undefined) {
    this._extra = extra;
  }

  addFilterListeners = (filter: Filter<unknown>) => {
    if (this._skipFilterListeners) {
      return [];
    }

    return [
      addEventListener(filter.events, 'change', () =>
        this.events.emit('change'),
      ),
      addEventListener(filter.events, 'refresh', () =>
        this.events.emit('refresh'),
      ),
      addEventListener(filter.events, 'scheduleRefresh', () =>
        this.events.emit('scheduleRefresh'),
      ),
    ];
  };
}
